import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  IOnboardingOption,
  IOnboardingSpecialistOccasion,
  IOnboardingSpecialistServiceLine,
} from "../../../Interfaces/IOnboardingWizardState";
import {
  needsServiceLineFloor,
  newId,
  optionLabel,
  specialistPanelLabel,
  TEST_TYPE_PRE_DEFINED,
  TEST_TYPE_TIME_OF_BOOKING,
} from "./Step2.rules";
import { optionMenuContent, renderOptionValue } from "./OptionMenu";

type Props = {
  method: "Hair" | "Nail";
  cards: IOnboardingSpecialistOccasion[];
  onChange: (cards: IOnboardingSpecialistOccasion[]) => void;
  occasions: IOnboardingOption[];
  testTypes: IOnboardingOption[];
  panels: IOnboardingOption[];
  periods: number[];
  fixedLab: string;
  frozen: boolean;
};

/**
 * Hair and nail as per-occasion cards (S2.7, story 5621).
 *
 * These are sold as predefined service lines rather than screened on site, so the cards never show a
 * collection, lab option, lab service or secondary control — and the laboratory is fixed, shown above
 * the cards. Time of Booking needs nothing further captured.
 */
const SpecialistOccasionCards: React.FC<Props> = ({
  method,
  cards,
  onChange,
  occasions,
  testTypes,
  panels,
  periods,
  fixedLab,
  frozen,
}) => {
  const [clipboard, setClipboard] = useState<IOnboardingSpecialistOccasion | null>(null);
  const [clipboardSource, setClipboardSource] = useState<string>("");
  const [notice, setNotice] = useState<string>("");

  const methodLabel = method === "Hair" ? "hair" : "nail";

  /** The panel label carries its method once (S2.7), open list and closed field alike. */
  const panelLabel = (key: string) => specialistPanelLabel(panels, key, method);

  const announce = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 3000);
  };

  const update = (index: number, next: IOnboardingSpecialistOccasion) =>
    onChange(cards.map((card, cardIndex) => (cardIndex === index ? next : card)));

  /** Pasted lines receive new ids so they are independent of the source (S2.8). */
  const cloneLines = (lines: IOnboardingSpecialistServiceLine[]) =>
    lines.map((line) => ({ ...line, id: newId() }));

  const onCopy = (card: IOnboardingSpecialistOccasion) => {
    setClipboard({ ...card, included: false });
    setClipboardSource(card.occasionCode);
    announce(`Copied ${optionLabel(occasions, card.occasionCode)}`);
  };

  const paste = (index: number) => {
    if (!clipboard) return;
    const target = cards[index];
    update(index, {
      ...clipboard,
      occasionCode: target.occasionCode,
      included: true,
      serviceLines: cloneLines(clipboard.serviceLines),
    });
    announce(`Pasted onto ${optionLabel(occasions, target.occasionCode)}`);
  };

  const pasteToAll = () => {
    if (!clipboard) return;
    onChange(
      cards.map((card) =>
        card.occasionCode === clipboardSource
          ? card
          : {
              ...clipboard,
              occasionCode: card.occasionCode,
              included: true,
              serviceLines: cloneLines(clipboard.serviceLines),
            },
      ),
    );
    announce(`Applied to all ${cards.length} occasions`);
  };

  const pasteToIncluded = () => {
    if (!clipboard) return;
    const targets = cards.filter((x) => x.included && x.occasionCode !== clipboardSource);
    if (!targets.length) {
      announce("No other occasions included");
      return;
    }
    onChange(
      cards.map((card) =>
        card.included && card.occasionCode !== clipboardSource
          ? {
              ...clipboard,
              occasionCode: card.occasionCode,
              included: true,
              serviceLines: cloneLines(clipboard.serviceLines),
            }
          : card,
      ),
    );
    announce(`Applied to ${targets.length} included occasions`);
  };

  /** The first line is prefilled as lab · test reason · method while empty (S2.7). */
  const newLine = (card: IOnboardingSpecialistOccasion, isFirst: boolean) => ({
    id: newId(),
    serviceName: isFirst
      ? `${fixedLab} · ${card.testReason || optionLabel(occasions, card.occasionCode)} · ${methodLabel}`
      : "",
    alternativeSamplePermitted: null,
    periodMonths: null,
    panelKey: null,
  });

  /**
   * Keeps an included Pre Defined occasion above its floor of one service line (S2.7).
   *
   * The card could sit on Pre Defined reading "Service lines (0)" with no line at all, so the app
   * held a state the step would then refuse — the floor was only enforced afterwards, by hiding
   * Remove once one line remained (bug 5712). S2.12's principle is that a rule is enforced by the
   * controls offered, never by a warning after the fact, so the line is there from the moment the
   * occasion becomes an included Pre Defined one.
   *
   * Both routes into that state go through here: ticking Include, and switching from Time of Booking
   * to Pre Defined on an occasion that is already included. Time of Booking needs no lines and is
   * left alone.
   */
  const withServiceLineFloor = (
    card: IOnboardingSpecialistOccasion,
  ): IOnboardingSpecialistOccasion => {
    if (!needsServiceLineFloor(card)) return card;
    return { ...card, serviceLines: [newLine(card, true)] };
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {method === "Hair" ? "Hair" : "Nail"} occasions
        </Typography>
        {/* The specialist laboratory is fixed in both countries (S2.11). */}
        <Chip size="small" variant="outlined" label={`Laboratory: ${fixedLab} (fixed)`} />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
        {clipboard ? (
          <>
            <Chip
              size="small"
              color="primary"
              label={`Holding ${optionLabel(occasions, clipboardSource)}`}
            />
            <Button size="small" disabled={frozen} onClick={pasteToAll}>
              Paste to all
            </Button>
            <Button size="small" disabled={frozen} onClick={pasteToIncluded}>
              Paste to included
            </Button>
            <Button
              size="small"
              onClick={() => {
                setClipboard(null);
                setClipboardSource("");
              }}
            >
              Clear
            </Button>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {/* Counted from the cards themselves: they follow the hair test reasons (story 6359),
                so a fixed number here goes stale the moment that list changes. */}
            {`Configure one card, press Copy on it, then paste it onto another card — or onto all ${cards.length}.`}
          </Typography>
        )}
        {notice && <Chip size="small" color="success" label={notice} />}
      </Box>

      {cards.map((card, index) => {
        const preDefined = card.testType !== TEST_TYPE_TIME_OF_BOOKING;
        return (
          <Paper
            key={card.occasionCode}
            elevation={0}
            sx={{ p: 2, mt: 1, border: "1px solid #eee", backgroundColor: card.included ? "#fff" : "#fafafa" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    disabled={frozen}
                    checked={card.included}
                    onChange={(e) => {
                      update(
                        index,
                        withServiceLineFloor({ ...card, included: e.target.checked }),
                      );
                    }}
                  />
                }
                label={`${optionLabel(occasions, card.occasionCode)} (${card.occasionCode})`}
              />
              <Box>
                <Button size="small" disabled={frozen || !card.included} onClick={() => onCopy(card)}>
                  {clipboardSource === card.occasionCode && clipboard ? "Copied" : "Copy"}
                </Button>
                {clipboard && clipboardSource !== card.occasionCode && (
                  <Button size="small" disabled={frozen} onClick={() => paste(index)}>
                    Paste
                  </Button>
                )}
              </Box>
            </Box>

            {card.included && (
              <>
                <RadioGroup
                  row
                  value={card.testType || TEST_TYPE_PRE_DEFINED}
                  // Switching to Pre Defined reaches the same zero-line state as ticking Include, so
                  // it goes through the same floor (bug 5712).
                  onChange={(e) =>
                    update(
                      index,
                      withServiceLineFloor({ ...card, testType: e.target.value }),
                    )
                  }
                >
                  {testTypes.map((option) => (
                    <FormControlLabel
                      key={option.key}
                      value={option.key}
                      control={<Radio size="small" disabled={frozen} />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                <Typography variant="caption" color="text.secondary" display="block">
                  {testTypes.find((x) => x.key === (card.testType || TEST_TYPE_PRE_DEFINED))?.description}
                </Typography>

                {preDefined && (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Test reason"
                      sx={{ mt: 1 }}
                      disabled={frozen}
                      value={card.testReason ?? ""}
                      placeholder={optionLabel(occasions, card.occasionCode)}
                      onChange={(e) => update(index, { ...card, testReason: e.target.value })}
                    />

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Service lines ({card.serviceLines.length})
                      </Typography>
                      <Box>
                        <Button
                          size="small"
                          disabled={frozen}
                          onClick={() =>
                            update(index, {
                              ...card,
                              serviceLines: [
                                ...card.serviceLines,
                                newLine(card, card.serviceLines.length === 0),
                              ],
                            })
                          }
                        >
                          Add service line
                        </Button>
                        <Button
                          size="small"
                          disabled={frozen || !card.serviceLines.length}
                          onClick={() => {
                            const last = card.serviceLines[card.serviceLines.length - 1];
                            update(index, {
                              ...card,
                              serviceLines: [
                                ...card.serviceLines,
                                { ...last, id: newId(), serviceName: `${last.serviceName} (copy)` },
                              ],
                            });
                          }}
                        >
                          Duplicate last
                        </Button>
                      </Box>
                    </Box>

                    {card.serviceLines.map((line, lineIndex) => (
                      <Box
                        key={line.id || lineIndex}
                        sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mt: 1 }}
                      >
                        <TextField
                          size="small"
                          required
                          label="Service name"
                          sx={{ minWidth: 240 }}
                          disabled={frozen}
                          value={line.serviceName ?? ""}
                          onChange={(e) =>
                            update(index, {
                              ...card,
                              serviceLines: card.serviceLines.map((x, i) =>
                                i === lineIndex ? { ...x, serviceName: e.target.value } : x,
                              ),
                            })
                          }
                        />
                        <TextField
                          select
                          size="small"
                          required
                          label="Period"
                          sx={{ minWidth: 120 }}
                          disabled={frozen}
                          value={line.periodMonths ?? ""}
                          onChange={(e) =>
                            update(index, {
                              ...card,
                              serviceLines: card.serviceLines.map((x, i) =>
                                i === lineIndex ? { ...x, periodMonths: Number(e.target.value) } : x,
                              ),
                            })
                          }
                        >
                          {periods.map((period) => (
                            <MenuItem key={period} value={period}>
                              {period} months
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          size="small"
                          required
                          label="Panel"
                          sx={{ minWidth: 220 }}
                          disabled={frozen}
                          value={line.panelKey ?? ""}
                          onChange={(e) =>
                            update(index, {
                              ...card,
                              serviceLines: card.serviceLines.map((x, i) =>
                                i === lineIndex ? { ...x, panelKey: e.target.value } : x,
                              ),
                            })
                          }
                          SelectProps={{ renderValue: renderOptionValue(panels, panelLabel) }}
                        >
                          {panels.map((panel) => (
                            <MenuItem key={panel.key} value={panel.key}>
                              {/* The label names the method once (S2.7, story 6359); the panel's own
                                  wording is what makes the list readable while it is open (task 5729). */}
                              {optionMenuContent(panels, panel.key, panelLabel(panel.key))}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          size="small"
                          required
                          label={method === "Hair" ? "Body hair allowed" : "Toenail allowed"}
                          sx={{ minWidth: 170 }}
                          disabled={frozen}
                          value={
                            line.alternativeSamplePermitted === null ||
                            line.alternativeSamplePermitted === undefined
                              ? ""
                              : line.alternativeSamplePermitted
                                ? "yes"
                                : "no"
                          }
                          onChange={(e) =>
                            update(index, {
                              ...card,
                              serviceLines: card.serviceLines.map((x, i) =>
                                i === lineIndex
                                  ? { ...x, alternativeSamplePermitted: e.target.value === "yes" }
                                  : x,
                              ),
                            })
                          }
                        >
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </TextField>
                        {/* Remove is hidden while only one line remains under Pre Defined (S2.7). */}
                        {card.serviceLines.length > 1 && (
                          <Button
                            size="small"
                            color="error"
                            disabled={frozen}
                            onClick={() =>
                              update(index, {
                                ...card,
                                serviceLines: card.serviceLines.filter((_, i) => i !== lineIndex),
                              })
                            }
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                    ))}
                  </>
                )}
              </>
            )}
          </Paper>
        );
      })}
    </Paper>
  );
};

export default SpecialistOccasionCards;
